import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TwoFactorAuthenticationService } from '../../api/generated/services/TwoFactorAuthenticationService';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';
import type { TwoFactorSetupView } from '../../api/generated/models/TwoFactorSetupView';

// Carries server-provided `errorCode` (e.g. `two_factor_locked_by_data_source`)
// alongside the human-readable message so the UI can branch by code, not by text.
export interface TwoFactorErrorPayload {
  message: string;
  errorCode?: string;
  status?: number;
}

interface TwoFactorState {
  setupData: TwoFactorSetupView | null;
  setupLoading: boolean;
  confirmLoading: boolean;
  disableLoading: boolean;
  error: string | null;
}

const initialState: TwoFactorState = {
  setupData: null,
  setupLoading: false,
  confirmLoading: false,
  disableLoading: false,
  error: null,
};

export const setup2FA = createAsyncThunk(
  'twoFactor/setup',
  async (_, { rejectWithValue }) => {
    try {
      return await TwoFactorAuthenticationService.postApp2FaSetup();
    } catch (error) {
      if (error instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(error);
        if (parsedBody && parsedBody.error) {
          return rejectWithValue(parsedBody.error);
        }
      }
      return rejectWithValue('Failed to setup 2FA');
    }
  }
);

export const confirm2FA = createAsyncThunk(
  'twoFactor/confirm',
  async (code: string, { rejectWithValue }) => {
    try {
      return await TwoFactorAuthenticationService.postApp2FaConfirm({ code });
    } catch (error) {
      if (error instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(error);
        if (parsedBody && parsedBody.error) {
          return rejectWithValue(parsedBody.error);
        }
      }
      return rejectWithValue('Invalid code');
    }
  }
);

export const disable2FA = createAsyncThunk<
  Awaited<ReturnType<typeof TwoFactorAuthenticationService.deleteApp2FaDisable>>,
  string,
  { rejectValue: TwoFactorErrorPayload }
>(
  'twoFactor/disable',
  async (code, { rejectWithValue }) => {
    try {
      return await TwoFactorAuthenticationService.deleteApp2FaDisable({ code });
    } catch (error) {
      if (error instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(error);
        return rejectWithValue({
          message: (parsedBody && parsedBody.error) ? parsedBody.error : (error.message || 'Invalid code'),
          errorCode: parsedBody && typeof parsedBody.errorCode === 'string' ? parsedBody.errorCode : undefined,
          status: error.status,
        });
      }
      return rejectWithValue({ message: 'Invalid code' });
    }
  }
);

const twoFactorSlice = createSlice({
  name: 'twoFactor',
  initialState,
  reducers: {
    resetTwoFactor: (state) => {
      state.setupData = null;
      state.setupLoading = false;
      state.confirmLoading = false;
      state.disableLoading = false;
      state.error = null;
    },
    clearSetupData: (state) => {
      state.setupData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setup2FA.pending, (state) => {
        state.setupLoading = true;
        state.error = null;
      })
      .addCase(setup2FA.fulfilled, (state, action) => {
        state.setupLoading = false;
        state.setupData = action.payload;
      })
      .addCase(setup2FA.rejected, (state, action) => {
        state.setupLoading = false;
        state.error = action.payload as string;
      })
      .addCase(confirm2FA.pending, (state) => {
        state.confirmLoading = true;
        state.error = null;
      })
      .addCase(confirm2FA.fulfilled, (state) => {
        state.confirmLoading = false;
        state.setupData = null;
      })
      .addCase(confirm2FA.rejected, (state, action) => {
        state.confirmLoading = false;
        state.error = action.payload as string;
      })
      .addCase(disable2FA.pending, (state) => {
        state.disableLoading = true;
        state.error = null;
      })
      .addCase(disable2FA.fulfilled, (state) => {
        state.disableLoading = false;
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.disableLoading = false;
        state.error = action.payload?.message ?? null;
      });
  },
});

export const { resetTwoFactor, clearSetupData } = twoFactorSlice.actions;
export default twoFactorSlice.reducer;
